import React, { useState, useEffect } from 'react'
import BreadCrumbComp from '../../../components/breadCrumb'
import { Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { callApiAsync, getInternalClientSatisfactionQuestions, updateInternalClientSatisfactionQuestions } from '../../../services/apiService'
import { getTodayYMD } from '../../../utils/dateUtils'
import FormTextField from '../../../components/textField/formTextField'
import LaddaButton from '../../../components/buttons/laddaButton'
import CustomSnackbar from '../../../components/snackbar'
import VerificationModal from '../../../components/modals/verificationModal'

const validationSchema = yup.object().shape({
    pregunta1: yup.string().trim().required('La pregunta 1 es requerida'),
    pregunta2: yup.string().trim().required('La pregunta 2 es requerida'),
    pregunta3: yup.string().trim().required('La pregunta 3 es requerida'),
    pregunta4: yup.string().trim().required('La pregunta 4 es requerida'),
    pregunta5: yup.string().trim().required('La pregunta 5 es requerida'),
    pregunta6: yup.string().trim().required('La pregunta 6 es requerida'),
    pregunta7: yup.string().trim().required('La pregunta 7 es requerida'),
    pregunta8: yup.string().trim().required('La pregunta 8 es requerida'),
    pregunta9: yup.string().trim().required('La pregunta 9 es requerida'),
    pregunta10: yup.string().trim().required('La pregunta 10 es requerida')
})

const CreateSurvey = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [originalQuestions, setOriginalQuestions] = useState([])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('success')
    const [openVerificationModal, setOpenVerificationModal] = useState(false)

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            pregunta1: '',
            pregunta2: '',
            pregunta3: '',
            pregunta4: '',
            pregunta5: '',
            pregunta6: '',
            pregunta7: '',
            pregunta8: '',
            pregunta9: '',
            pregunta10: ''
        }
    })

    useEffect(() => {
        loadQuestions()
    }, [])

    const loadQuestions = async () => {
        setLoading(true)
        await callApiAsync(
            () => getInternalClientSatisfactionQuestions(),
            (resp) => {
                const data = resp?.data ?? resp
                const questions = Array.isArray(data) ? data : []

                // Si no hay preguntas, inicializar con valores vacíos
                if (questions.length === 0) {
                    setOriginalQuestions([])
                    // Inicializar todos los campos con valores vacíos
                    for (let i = 1; i <= 10; i++) {
                        setValue(`pregunta${i}`, '')
                    }
                    setLoading(false)
                    return
                }

                // Ordenar por questionNumber para asegurar el orden correcto
                const sortedQuestions = [...questions].sort((a, b) => a.questionNumber - b.questionNumber)
                setOriginalQuestions(sortedQuestions)

                // Cargar las preguntas en los campos del formulario
                sortedQuestions.forEach((question) => {
                    const fieldName = `pregunta${question.questionNumber}`
                    setValue(fieldName, question.questionText || '')
                })

                // Asegurar que todos los campos estén inicializados (por si faltan algunas preguntas)
                for (let i = 1; i <= 10; i++) {
                    const existingQuestion = sortedQuestions.find(q => q.questionNumber === i)
                    if (!existingQuestion) {
                        setValue(`pregunta${i}`, '')
                    }
                }

                setLoading(false)
            },
            (error) => {

                setLoading(false)
            }
        )
    }

    const hasChanges = () => {
        if (originalQuestions.length === 0) return false
        const values = getValues()

        for (let i = 0; i < originalQuestions.length; i++) {
            const question = originalQuestions[i]
            const fieldName = `pregunta${question.questionNumber}`
            const currentValue = values[fieldName] || ''
            const originalValue = question.questionText || ''

            if (currentValue.trim() !== originalValue.trim()) {
                return true
            }
        }
        return false
    }

    const onSubmit = async (formData) => {
        if (!hasChanges()) {
            setSnackbarMessage('No hay cambios en la encuesta')
            setSnackbarSeverity('info')
            setSnackbarOpen(true)
            return
        }

        setOpenVerificationModal(true)
    }

    const handleConfirmSave = async () => {
        setOpenVerificationModal(false)
        setLoading(true)

        const formData = getValues()

        const questionsToSend = originalQuestions.map((question, index) => ({
            id: question.id,
            questionNumber: question.questionNumber,
            questionText: formData[`pregunta${question.questionNumber}`] || ''
        }))

        await callApiAsync(
            () =>
                updateInternalClientSatisfactionQuestions({
                    questions: questionsToSend,
                    creation_date: getTodayYMD()
                }),
            () => {
                setSnackbarMessage('Encuesta actualizada correctamente')
                setSnackbarSeverity('success')
                setSnackbarOpen(true)
                setLoading(false)
                loadQuestions()
            },
            (error) => {

            }
        )
    }

    const handleCancelSave = () => {
        setOpenVerificationModal(false)
    }

    return (
        <div>
            <BreadCrumbComp />
            <div className="searchTitle">
                <h1>EDITAR ENCUESTA SATISFACCIÓN CLIENTE INTERNO</h1>
            </div>

            <div className="table-container">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-20">
                        <h2 className="title-bio-details-chard" style={{ marginBottom: '20px' }}>
                            ENCUESTA SATISFACCIÓN CLIENTE INTERNO
                        </h2>

                        <Grid container spacing={3}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <Grid item xs={12} key={num}>
                                    <div className="mb-15">
                                        <p className="select-form-label">Pregunta {num}</p>
                                        <FormTextField
                                            name={`pregunta${num}`}
                                            control={control}
                                            isTextArea={true}
                                            rows={2}
                                            margin={"0"}
                                            error={errors[`pregunta${num}`]}
                                        />
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    </div>

                    <div className="HContainer j-center" style={{ marginTop: '30px' }}>
                        <LaddaButton
                            type="submit"
                            variant="contained"
                            backgroundColor="#6C5CE7"
                            height="50px"
                            borderRadius="15px"
                            width="400px"
                            disabled={loading}
                        >
                            <div className="IHContainer align-center">
                                <p className="button_text">Guardar Cambios</p>
                            </div>
                        </LaddaButton>
                    </div>
                </form>
            </div>

            <CustomSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />

            <VerificationModal
                open={openVerificationModal}
                handleClose={handleCancelSave}
                alertText="¿Estás seguro de cambiar esta versión de encuesta?"
                onConfirm={handleConfirmSave}
                onCancel={handleCancelSave}
                iconSeverity="warning"
                yesNo={true}
            />
        </div>
    )
}

export default CreateSurvey
